import { FC, Fragment, SVGProps, useContext, useEffect, useState } from 'react';

import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import { Typography, alpha, useTheme } from '@mui/material';
import type { Theme } from '@mui/material';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Link, useLocation } from 'react-router-dom';
import type { Location } from 'react-router-dom';

import { MetadataError } from '@ep/error-handling';
import type { NavigationLink, PBIReport } from '@esg/esg-global-types';

import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import { UserContext } from '../../../context/UserContext';
import { CompanyContext } from '../../../context/CompanyContext';
import { GroupContext } from '../../../context/GroupContext';
import {
  Module,
  View,
  ViewExtended,
  modules,
  resolveViewName,
  hasCustomDashboard,
  CUSTOM_DASHBOARD_VIEW_ID
} from '../../../lib/app/navigation';
import { getPBIReports } from '../../../lib/app/powerbi/pbi_report';
import { getNavigationLinks } from '../../../lib/app/navigation_link';
import { generateUuidV4 } from '../../../util/uuid';
import { log } from '../../../util/log';
import { getUserViews, isViewAvailable } from '../../../util/user_access';
import { ReactComponent as HomeIcon } from '../../../assets/icons/Home.svg';
import ExternalNavigationModal from '../../shared/modal/ExternalNavigationModal';
import PrimaryDivider from '../../shared/decoration/PrimaryDivider';
import PrimarySkeleton from '../../shared/loading/PrimarySkeleton';
import { MinorHeading } from '../../shared/typography/PrimaryTypography';

interface SideMenuNavItemsProps {
  handleCloseNav: () => void;
}

/**
 * Display the modules and views available for a user to navigate to within the app.
 * @param {(): void} handleCloseNav Close the left navigation menu in parent.
 * @returns {JSX.Element}
 */
const SideMenuNavItems = ({ handleCloseNav }: SideMenuNavItemsProps) => {
  const { setFeedbackData } = useContext(FeedbackSnackbarContext);
  const { user } = useContext(UserContext);
  const { group } = useContext(GroupContext);
  const { company } = useContext(CompanyContext);
  const [pbiReports, setPBIReports] = useState<Array<PBIReport>>([]);
  const [navigationLinks, setNavigationLinks] = useState<Array<NavigationLink>>([]);
  const [openModule, setOpenModule] = useState<string>('');
  const [displayNavigationModal, setDisplayNavigationModal] = useState<boolean>(false);
  const theme: Theme = useTheme();
  const primary_blue: string = theme.palette.primary.main;

  //current route of app
  const location: Location = useLocation();
  const current_route: string = location.pathname.substring(1);

  const tridium_link: string | undefined = navigationLinks.find(
    (nav_link: NavigationLink) => nav_link.type === 'tridium'
  )?.url;

  const handleOpenModule = (module_id: string): void => {
    setOpenModule(openModule === module_id ? '' : module_id);
  };

  const handleNavigationLinkClick = (): void => {
    setDisplayNavigationModal(true);
  };

  useEffect(() => {
    try {
      (async () => {
        if (group && company) {
          const pbi_reports: Array<PBIReport> = await getPBIReports(group.id, company.id);
          const navigation_links: Array<NavigationLink> = await getNavigationLinks(
            group.id,
            company.id
          );
          setPBIReports(pbi_reports);
          setNavigationLinks(navigation_links);
        }
      })().catch((error) => {
        throw new Error(error);
      });
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: SideMenuNavItems failed on an unknown error while initialising.',
          null,
          tracking_id
        )
      );
      setFeedbackData({
        message: `An error occurred. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    }
    return;
  }, [company]);

  return (
    <Fragment>
      <ExternalNavigationModal
        open={displayNavigationModal}
        title="Continue to Syntiro Live?"
        description="You are about to be redirected to the Syntiro Live portal in a new tab. Are you sure you want to continue?"
        external_link={tridium_link ?? ''}
        handleCloseModal={() => setDisplayNavigationModal(false)}
      />
      <div data-testid="sidemenunavitems_id" />
      {user && group && company ? (
        <>
          <Link to={'/'} key={'home'} style={{ textDecoration: 'none' }}>
            <ListItemButton
              onClick={() => {
                handleCloseNav();
              }}
              sx={{
                color: theme.palette.primary.main
              }}
            >
              <ListItemIcon sx={{ mr: 1 }}>
                <HomeIcon
                  stroke={theme.palette.primary.main}
                  fill={theme.palette.primary.main}
                  width={45}
                />
              </ListItemIcon>
              <MinorHeading>Home</MinorHeading>
            </ListItemButton>
          </Link>
          {
            <List sx={{ width: '100%' }} component="nav">
              {modules
                .filter((module: Module) => !module.require_super_admin || user?.super_admin)
                .map((module: Module) => {
                  const extendedViews: ViewExtended[] = module.views.map((view: View) => ({
                    ...view,
                    module
                  }));
                  const allowedViews: View[] = getUserViews(
                    user,
                    group.id,
                    company.id,
                    extendedViews,
                    pbiReports
                  ).filter(
                    (view) => isViewAvailable(view.id, user, group.id, company.id) && !view.disabled
                  );
                  const allowedViewIdSet = new Set(allowedViews.map((view: View) => view.id));
                  const visibleDisabledViews: View[] = module.views.filter(
                    (view) =>
                      !allowedViewIdSet.has(view.id) &&
                      (view.id !== CUSTOM_DASHBOARD_VIEW_ID || hasCustomDashboard(company.id)) &&
                      !(
                        view.hideWhenPbiMissing &&
                        !pbiReports.some((report) => report.view === view.id)
                      ) &&
                      (!view.hideWhenInaccessible ||
                        isViewAvailable(view.id, user, group.id, company.id))
                  );
                  const ModuleIcon: FC<SVGProps<SVGSVGElement>> = module.icon;
                  return (
                    <Fragment key={module.id}>
                      {module.views.length > 0 ? (
                        <>
                          <ListItemButton
                            key={`item-${module.id}`}
                            onClick={() => handleOpenModule(module.id)}
                            sx={{
                              color: theme.palette.primary.main,
                              py: 2
                            }}
                          >
                            <ListItemIcon>
                              <ModuleIcon
                                style={{ width: 45, stroke: primary_blue, fill: primary_blue }}
                              />
                            </ListItemIcon>
                            <MinorHeading sx={{ ml: 1, mr: 'auto' }}>{module.name}</MinorHeading>
                            {openModule === module.id ? (
                              <ArrowDropUpIcon color="primary" />
                            ) : (
                              <ArrowDropDownIcon color="primary" />
                            )}
                          </ListItemButton>
                          <Collapse
                            in={openModule === module.id}
                            timeout="auto"
                            unmountOnExit
                            key={`collapse-${module.id}`}
                          >
                            <List component="div" key={`list-${module.id}-views`} disablePadding>
                              {allowedViews.map((view: View) => {
                                return view.id === 'syntiroLive' ? (
                                  tridium_link && (
                                    <Fragment key={view.id}>
                                      <ListItemButton
                                        key={view.id}
                                        sx={{ pl: 8, py: 2 }}
                                        onClick={handleNavigationLinkClick}
                                      >
                                        <Typography
                                          sx={{
                                            color: theme.palette.primary.main,
                                            fontSize: theme.typography.caption.fontSize
                                          }}
                                        >
                                          {resolveViewName(view, company?.id)}
                                        </Typography>
                                      </ListItemButton>
                                      <PrimaryDivider opacity={0.3} key={`${view.id}-divider`} />
                                    </Fragment>
                                  )
                                ) : (
                                  <Fragment key={view.id}>
                                    <Link
                                      to={`${module.route}/${view.route}`}
                                      key={`${view.id}-link`}
                                      style={{ textDecoration: 'none' }}
                                    >
                                      <ListItemButton
                                        sx={{
                                          pl: 8,
                                          py: 2,
                                          backgroundColor: current_route.endsWith('/' + view.route)
                                            ? alpha(theme.palette.secondary.main, 0.1)
                                            : 'transparent'
                                        }}
                                        onClick={handleCloseNav}
                                      >
                                        <Typography
                                          sx={{
                                            color: theme.palette.primary.main,
                                            fontSize: theme.typography.caption.fontSize
                                          }}
                                        >
                                          {resolveViewName(view, company?.id)}
                                        </Typography>
                                      </ListItemButton>
                                    </Link>
                                    <PrimaryDivider key={`${view.id}-divider`} opacity={0.3} />
                                  </Fragment>
                                );
                              })}
                              {visibleDisabledViews.map((view: View) => (
                                <Fragment key={view.id}>
                                  <ListItemButton
                                    sx={{
                                      pl: 8,
                                      py: 2
                                    }}
                                    disabled
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: theme.typography.caption.fontSize
                                      }}
                                    >
                                      {resolveViewName(view, company?.id)}
                                    </Typography>
                                  </ListItemButton>
                                  <PrimaryDivider key={`${view.id}-divider`} opacity={0.3} />
                                </Fragment>
                              ))}
                            </List>
                          </Collapse>
                        </>
                      ) : (
                        <>
                          <Link
                            to={module.route}
                            key={module.id}
                            style={{ textDecoration: 'none' }}
                          >
                            <ListItemButton
                              onClick={() => {
                                handleOpenModule(module.id);
                                handleCloseNav();
                              }}
                              sx={{
                                color: theme.palette.primary.main,
                                py: 2
                              }}
                            >
                              <ListItemIcon>
                                <ModuleIcon
                                  style={{ width: 45, stroke: primary_blue, fill: primary_blue }}
                                />
                              </ListItemIcon>
                              <MinorHeading sx={{ ml: 1, mr: 'auto' }}>{module.name}</MinorHeading>
                            </ListItemButton>
                          </Link>
                        </>
                      )}
                    </Fragment>
                  );
                })}
            </List>
          }
        </>
      ) : (
        <PrimarySkeleton
          variant="rounded"
          opacity={1}
          sx={{ height: 50, width: '95%', mx: 'auto' }}
        />
      )}
    </Fragment>
  );
};

export default SideMenuNavItems;
